import { useCallback, useEffect, useRef } from 'react';

import { isBizyConversationStale } from '../lib/bizy-activity-stamp';

interface UseBizyIdleBlankSlateOptions {
	/**
	 * Arms the predicate. The caller gates this on: panel open, chat view,
	 * settled non-empty timeline, not loading/streaming/timed out, greeter not
	 * showing, and not already in the blank slate — so entering the blank
	 * slate disarms it until the next conversation activity re-enables it.
	 */
	enabled: boolean;
	/**
	 * True while the user has draft text in the composer. Composing skips
	 * evaluation — never yank the viewport mid-composition.
	 */
	isComposing: boolean;
	/** Fired when an evaluation finds the conversation stale. */
	onIdle: () => void;
}

/**
 * Trigger for the idle blank slate (BIZY-683). Evaluates "has the user
 * conversed with Bizy in the last hour?" (the localStorage activity
 * stamp — see bizy-activity-stamp.ts) at exactly two kinds of moments:
 *
 *   1. Arming transitions (enabled false→true): the panel opened, a hard
 *      refresh landed on a settled timeline, the user switched back to the
 *      chat view.
 *   2. Window focus / visibilitychange→visible while armed: returning to the
 *      tab after being away.
 *
 * No countdown timer — a focused-but-idle panel never moves out from under a
 * user reading the old conversation.
 */
export function useBizyIdleBlankSlate({ enabled, isComposing, onIdle }: UseBizyIdleBlankSlateOptions) {
	const onIdleRef = useRef(onIdle);
	onIdleRef.current = onIdle;
	const isComposingRef = useRef(isComposing);
	isComposingRef.current = isComposing;

	const evaluate = useCallback(() => {
		if (isComposingRef.current) return;
		if (isBizyConversationStale()) onIdleRef.current();
	}, []);

	// Moment 1: arming transition.
	useEffect(() => {
		if (enabled) evaluate();
	}, [enabled, evaluate]);

	// Moment 2: returning to the window/tab while armed.
	useEffect(() => {
		if (!enabled) return;
		const handleFocus = () => evaluate();
		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible') evaluate();
		};
		window.addEventListener('focus', handleFocus);
		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => {
			window.removeEventListener('focus', handleFocus);
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, [enabled, evaluate]);
}
